/**
 * Foundation: shared attachment types and pure helpers.
 *
 * Single source of truth for the *rendered / persisted* shape of a chat
 * attachment. The composer side (file picker, drag, paste) lives in
 * {@link ./chat-attachment-classifier.ts} and decides *acceptance* (size,
 * count, readability). This module decides *what the renderer sees* — id,
 * stable url, optional dimensions, and the four render kinds the chat
 * surface supports.
 *
 * Epic: JAISIU-3762 — Attachments in Jaisiu chat (ChatGPT-like)
 * Ticket: JAISIU-3763 (foundation)
 *
 * ## Hard rules
 *
 *  - **No DOM.** This file is `pure`. Renderers (JAISIU-3766, 3764) build
 *    elements; viewers (JAISIU-3767) wire interactions; persistence
 *    (JAISIU-3768) serialises to IndexedDB. None of that lives here.
 *  - **No `File` / no `Blob`.** This is the shape *after* the upload, with
 *    a stable URL the renderer can put in an `<img src>` or `<a href>`.
 *  - **Stable ids.** `id` is opaque to the renderer but must be unique
 *    within a message and stable across the persistence round-trip.
 *
 * ## Render kinds
 *
 *   - `image`        — inline render, capped to 360×480, never stretched.
 *                      Opens the in-app preview viewer on click.
 *   - `pdf`          — file chip with a "PDF" badge. Click opens the inline
 *                      viewer (iframe + download button).
 *   - `audio`        — file chip with an "AUDIO" badge. Click opens the
 *                      overlay's audio body (`<audio controls>`).
 *   - `video`        — file chip with a "VIDEO" badge. Click opens the
 *                      overlay's video body (`<video controls>`).
 *   - `spreadsheet`  — file chip with an "EXCEL" badge. Click opens the
 *                      overlay's spreadsheet body (iframe embed with
 *                      Office Web Viewer fallback for public URLs).
 *   - `document`     — file chip with a "DOC" badge. Click opens the
 *                      overlay's document body (same strategy as
 *                      spreadsheet).
 *   - `code`         — file chip with a "code" badge. No preview; downloads.
 *   - `file`         — generic file chip. No preview; downloads.
 *
 * `code` is decided from the MIME (text/* and a short list of common
 * source languages); everything else non-image, non-pdf falls into
 * `file`. The classifier already returns a usable MIME via
 * `effectiveMimeFor`; the kind decision is here because it is purely a
 * rendering concern.
 */

import type { IconName } from "../icons.ts";

/** The render kinds the chat surface knows how to draw. */
export type AttachmentKind =
  | "image"
  | "audio"
  | "video"
  | "pdf"
  | "spreadsheet"
  | "document"
  | "code"
  | "file"
  /** §15 GIF treated as image for preview; distinct for capability tables. */
  | "gif";

/** §15 persisted attachment lifecycle (Polish labels in UI). */
export type AttachmentPersistedStatus = "processing" | "ready" | "unsupported" | "error";

export function attachmentPersistedStatusLabel(status: AttachmentPersistedStatus): string {
  switch (status) {
    case "processing":
      return "Przetwarzanie";
    case "ready":
      return "Gotowy";
    case "unsupported":
      return "Nieobsługiwany";
    case "error":
      return "Błąd";
  }
}

/** Derive §15 status from persisted fields when `status` was not stamped yet. */
export function resolveAttachmentPersistedStatus(att: {
  status?: AttachmentPersistedStatus;
  uploading?: boolean;
  error?: string;
  url?: string;
}): AttachmentPersistedStatus {
  if (att.status) {
    return att.status;
  }
  if (att.error?.trim()) {
    return "error";
  }
  if (att.uploading) {
    return "processing";
  }
  if (att.url?.trim()) {
    return "ready";
  }
  return "processing";
}

/**
 * JAISIU-types-sweep: the gateway's coarser file family, returned by
 * `POST /api/session/files`. A superset of the render-kind vocabulary,
 * carried on the same `ChatAttachment.kind` field while a composer chip
 * is still in flight. Declared locally (structurally identical to
 * `session-files.ts::SessionFileKind`) so this module stays `pure` and
 * import-cycle free.
 */
export type SessionFileKind =
  | "image"
  | "video"
  | "audio"
  | "document"
  | "tabular"
  | "text"
  | "binary";

/**
 * A persisted chat attachment, ready to be rendered inline with its
 * parent message.
 *
 * `id` and `messageId` are opaque strings; the persistence layer
 * (JAISIU-3768) is responsible for assigning them. `url` is whatever the
 * server returned after upload — signed URL, EFSS handle, blob: URL from
 * the cache, all fine. `thumbnailUrl` is optional; renderers fall back to
 * `url` for images. `width` / `height` are intrinsic pixel dimensions
 * (for images only); renderers must not upscale past these.
 */
export interface ChatAttachment {
  id: string;
  /**
   * JAISIU-types-sweep: owning message id, stamped by the persistence
   * layer. Optional because the chat composer builds its chips before a
   * message row exists; `chat-attachment-persistence.ts` skips the
   * record entirely when `messageId` is missing
   * (`typeof v.messageId !== "string"` → `false`).
   */
  messageId?: string;
  /**
   * JAISIU-types-sweep: render kind on a persisted attachment, coarse
   * server-side family on an in-flight composer chip — the gateway's
   * `SessionFileKind` taxonomy (`image` | `video` | `audio` | `document`
   * | `tabular` | `text` | `binary`) is what `StoredAttachmentResult`
   * hands the composer, and `sessionFileIconName` already switches on the
   * union. Optional — `attachmentKindForMime(at.mimeType ?? "")` is the
   * fallback every consumer applies before drawing an icon.
   */
  kind?: AttachmentKind | SessionFileKind | string;
  /**
   * JAISIU-types-sweep: MIME as reported by the upload/store endpoint.
   * Optional because the pre-upload placeholder only carries `mimeType`;
   * `controllers/chat.ts` reads `att.mimeType ?? att.mime ?? ""`, so an
   * absent canonical `mime` degrades to an empty prompt-context MIME
   * rather than throwing.
   */
  mime?: string;
  /**
   * JAISIU-types-sweep: canonical filename. Optional because the
   * pre-upload placeholder only carries `name`;
   * `controllers/chat.ts` coalesces with `att.filename ?? att.name ?? "file"`
   * and `attachmentDisplayName` falls back to `"attachment"`.
   */
  filename?: string;
  /**
   * JAISIU-types-sweep: canonical byte size. Optional because the
   * pre-upload placeholder only carries `size`;
   * `controllers/chat.ts` coalesces with `att.size ?? att.sizeBytes ?? 0`
   * and the chips render `formatChipBytes(att.sizeBytes ?? 0)`.
   */
  sizeBytes?: number;
  /**
   * JAISIU-types-sweep: stable render URL. Optional because an
   * in-flight composer placeholder has only a `blob:`/data URL
   * (`previewUrl` / `dataUrl`) until the store round-trip returns
   * `url`; the renderers coalesce to `""` which yields an inert,
   * non-navigating `href`/`src` on a chip that has nothing to show yet.
   */
  url?: string;
  thumbnailUrl?: string;
  /** Intrinsic pixel width — only set for image attachments. */
  width?: number;
  /** Intrinsic pixel height — only set for image attachments. */
  height?: number;
  /**
   * JAISIU-types-sweep: in-flight upload placeholders. Set on the
   * narrow type to keep `state.chatAttachments[i].mimeType` /
   * `.dataUrl` / `.name` / `.size` consumers (controller/chat.ts,
   * chat-message-wiring) happy. Optional — the controller drops
   * them once the upload is committed and the
   * `sessionFileId` is populated.
   */
  mimeType?: string;
  /**
   * JAISIU-types-sweep: filename alias used by the in-flight chip.
   * Redundant with `filename` because `chat-compose-attachments.ts`
   * chose the wide spelling; kept here so the controller can read
   * whichever the producer set.
   */
  name?: string;
  /**
   * JAISIU-types-sweep: byte-size alias (`size` vs `sizeBytes`).
   * The narrow canonical is `sizeBytes`; `size` is the wide
   * spelling the chat composer used pre-migration.
   */
  size?: number;
  /**
   * JAISIU-types-sweep: inline base64 data URL of the chosen file
   * while the upload is in flight. Only present on
   * `ChatComposerAttachment` (pre-upload). The controller discards
   * it the moment `sessionFileId` is set.
   */
  dataUrl?: string;
  /**
   * JAISIU-types-sweep: server-side file id returned by
   * `POST /api/session/files`. Only set after the upload commits;
   * prior to that the optimistic message references the file by its
   * client-generated id and the controller inserts it on commit.
   */
  sessionFileId?: string;
  /**
   * JAISIU-types-sweep: error message from the failed upload, if
   * any. Drives the chip's red-state presentation. Optional — not
   * set when the upload succeeded.
   */
  error?: string;
  /**
   * JAISIU-types-sweep: in-flight upload spinner flag. Drives the
   * chat-composer's chip "uploading…" state. Optional — `false`
   * once the upload commits and `sessionFileId` is set.
   */
  uploading?: boolean;
  /** §15 persisted lifecycle when stamped by the store layer. */
  status?: AttachmentPersistedStatus;
  /**
   * JAISIU-types-sweep: alias for `id`, used by the optimistic
   * message-wiring path that pairs attach-ids with row-ids. Optional
   * because the chat-message-store stamps its own id and the composer
   * goes through `chatAttachments[i].id`.
   */
  attachmentId?: string;
  /**
   * JAISIU-types-sweep: render URL for the chip / transcript thumbnail.
   * Normally the gateway's `/api/session/files/<id>/preview` derivative;
   * a `blob:` URL is the transient in-flight fallback that
   * `chat-composer-attachments.ts` revokes on remove. Optional — only
   * set once `onAttachmentUpload` has resolved with a preview URL.
   */
  previewUrl?: string;
  /**
   * JAISIU-types-sweep: true when the gateway produced a real thumbnail
   * for this file, so the chip skips the generic type icon. Optional —
   * `Boolean(stored.previewUrl)` is the only thing that sets it.
   */
  hasPreview?: boolean;
  /**
   * JAISIU-4427: server-extracted text for `spreadsheet` and
   * `document` attachments. The gateway's session-file prepare step
   * already produces this for the `tabular` / `document` families;
   * the preview overlay renders it locally instead of embedding the
   * file in a third-party Office viewer (defect D8 — that would ship
   * an authenticated session-file URL to Microsoft).
   *
   * Optional — absent whenever extraction has not run or produced
   * nothing. The overlay says so explicitly rather than implying the
   * file was read.
   */
  text?: string;
  /**
   * JAISIU-4449 — the *prepared tabular* view of a spreadsheet
   * attachment: the column names, the total row count, and a sample
   * of the rows.
   *
   * The gateway's session-file prepare step already produces this
   * (`prep/schema.json`); the Control UI reads it through
   * `GET /api/session/files/<id>/content` and renders it as a real
   * table instead of a wall of CSV text.
   *
   * Optional — absent for non-tabular kinds, for files whose prep
   * never ran, and for files whose extraction produced nothing. The
   * overlay then falls back to the honest "not analysed" message.
   */
  tabular?: TabularPreview;
  /**
   * JAISIU-4479 — the *structured* view of a document attachment: the
   * headings, lists, paragraphs and tables the gateway extracted from
   * the file's OOXML.
   *
   * The gateway's prepare step writes this as the `prep/document.json`
   * derivative and serves it as `preview.blocks` alongside the flat
   * extracted text. When it is present the preview renders the
   * document's *shape* (real headings, real list numbering, real table
   * cells) instead of one flat string.
   *
   * Optional — absent for a PDF, a plain-text file, a DOCX whose OOXML
   * could not be parsed, and for a file whose prep never ran. The
   * overlay then falls back to the flat extracted text, and — when
   * there is no text either — to the honest "not analysed" message.
   */
  blocks?: DocumentBlock[];
}

/**
 * JAISIU-4479 — one structured block of a document, in document order.
 *
 * Mirrors the gateway's `SessionFileDocumentBlock` so the wire payload
 * and the render model cannot drift:
 *
 *   { type: "heading", level: 1..6, text }
 *   { type: "paragraph", text }
 *   { type: "list", ordered: boolean, items: [{ marker, level, text }] }
 *   { type: "table", rows: string[][] }
 *
 * `marker` is the number / bullet **as displayed** ("1.", "a)", "•"),
 * and `level` is the 1-based nesting depth of the list item.
 */
export type DocumentBlock =
  | { type: "heading"; level: 1 | 2 | 3 | 4 | 5 | 6; text: string }
  | { type: "paragraph"; text: string }
  | {
      type: "list";
      ordered: boolean;
      items: { marker: string; level: number; text: string }[];
    }
  | { type: "table"; rows: string[][] };

/**
 * JAISIU-4449 — the prepared tabular preview of a spreadsheet.
 *
 * Mirrors the gateway's `SessionFileTabularPreview` payload:
 * `columns` are the prepared column names, `rowCount` is the total
 * number of rows the file holds, and `sampleRows` is the (truncated)
 * sample the gateway prepared for display.
 */
export interface TabularPreview {
  /** Prepared column names, in file order. */
  columns: string[];
  /** Total number of rows in the file (may exceed `sampleRows.length`). */
  rowCount: number;
  /** Sample rows; each entry is one row of cell strings. */
  sampleRows: string[][];
  /**
   * JAISIU-3533 — every data row of the primary sheet.
   *
   * The gateway now ships the whole table, not a five-row excerpt, so the
   * operator sees the entire sheet. Absent for older gateways; the
   * renderer then falls back to `sampleRows`.
   */
  rows?: string[][];
  /**
   * JAISIU-3533 — the rows that sat *above* the detected header (title /
   * account / date-range preamble). Rendered as context above the table
   * so the operator sees the sheet's metadata instead of it vanishing.
   */
  preamble?: string[][];
  /**
   * JAISIU-3533 — every sheet of a multi-sheet workbook, so the operator
   * can switch tabs the way Excel does.
   */
  sheets?: TabularSheetPreview[];
}

/**
 * JAISIU-3533 — one sheet of a (possibly multi-sheet) workbook.
 */
export interface TabularSheetPreview {
  /** The tab name as it appears in the workbook. */
  name: string;
  /** Zero-based position in the workbook. */
  index: number;
  /** This sheet's column names. */
  columns: string[];
  /** This sheet's data rows. */
  rows: string[][];
  /** How many data rows the sheet holds. */
  rowCount: number;
  /** Rows above this sheet's header. */
  preamble: string[][];
}

/**
 * Pick the render kind from a MIME string.
 *
 * The composer's `effectiveMimeFor` (see `chat-attachment-classifier.ts`)
 * already does extension rescue for empty / `application/octet-stream`
 * declarations. We trust whatever MIME landed here is the display MIME
 * the classifier would have produced; this function only maps it to a
 * render kind.
 *
 * Order matters: `pdf` is checked before the generic image check because
 * some browsers tag a PDF as `application/octet-stream` and the
 * classifier extension table rescues it to `application/pdf` upstream.
 */
export function attachmentKindForMime(mime: string): AttachmentKind {
  const m = (mime ?? "").toLowerCase().trim();
  if (!m) return "file";
  if (m === "application/pdf") return "pdf";
  if (m.startsWith("image/")) return "image";
  // Audio + video (JAISIU-4427 / JAISIU-4428): every subtype the chat
  // preview overlay can play back via native `<audio>` / `<video controls>`.
  // Sub-MIME detail (mp3 vs wav, mp4 vs webm) is irrelevant — the browser
  // picks the codec; we only need to route the chip to the right body.
  // Ordered ahead of the `text/*` branches because some containers report
  // audio/video under text-ish MIME types.
  if (m.startsWith("audio/")) return "audio";
  if (m.startsWith("video/")) return "video";
  // Spreadsheet — Excel binary + Excel OOXML + ODS + CSV/TSV. CSV is also
  // a `text/*` MIME but the operator wants a sheet preview rather than a
  // `code` chip, so this runs *before* the generic `text/*` branch.
  if (
    m === "application/vnd.ms-excel" ||
    m === "application/vnd.ms-excel.sheet.macroenabled.12" ||
    m === "application/vnd.ms-excel.sheet.binary.macroenabled.12" ||
    m === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    m === "application/vnd.openxmlformats-officedocument.spreadsheetml.template" ||
    m === "application/vnd.oasis.opendocument.spreadsheet" ||
    m === "text/csv" ||
    m === "text/x-csv" ||
    m === "text/tab-separated-values" ||
    m === "application/csv"
  ) {
    return "spreadsheet";
  }
  // Document — Word binary, Word OOXML, OpenDocument text, RTF, EPUB.
  if (
    m === "application/msword" ||
    m === "application/vnd.ms-word.document.macroenabled.12" ||
    m === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    m === "application/vnd.openxmlformats-officedocument.wordprocessingml.template" ||
    m === "application/vnd.oasis.opendocument.text" ||
    m === "application/rtf" ||
    m === "text/rtf" ||
    m === "application/epub+zip"
  ) {
    return "document";
  }
  // Code: text/* and a short list of well-known source types that some
  // servers label with `application/x-...` rather than `text/...`.
  if (m.startsWith("text/")) return "code";
  if (
    m === "application/json" ||
    m === "application/xml" ||
    m === "application/javascript" ||
    m === "application/typescript" ||
    m === "application/x-shellscript" ||
    m === "application/x-yaml" ||
    m === "application/x-toml"
  ) {
    return "code";
  }
  return "file";
}

/**
 * Display name for an attachment.
 *
 * Strips any directory prefix (paths in `filename` are common when a
 * user drags a file from a file manager), trims whitespace, and falls
 * back to a placeholder for empty / missing names. The result is what
 * chips and tooltips show; the raw `filename` is still kept on the
 * record for the actual download.
 */
export function attachmentDisplayName(a: Pick<ChatAttachment, "filename">): string {
  const raw = (a.filename ?? "").trim();
  if (!raw) return "attachment";
  // Drop any path prefix — be tolerant of both POSIX and Windows.
  const base = raw.split(/[\\/]/).pop() ?? raw;
  return base || "attachment";
}

/**
 * Inline icon glyph for a kind. Returns a single character the chip
 * renders as a leading icon — chosen so it is meaningful without
 * needing an icon font or asset pipeline.
 *
 * JAISIU-types-sweep: accepts the gateway's coarser `SessionFileKind`
 * family and `undefined` too, because an in-flight composer chip can
 * carry either (see `ChatAttachment.kind`). Anything outside the four
 * render kinds falls through to the generic glyph.
 */
export function attachmentIconForKind(
  kind: AttachmentKind | SessionFileKind | string | undefined,
): string {
  switch (kind) {
    case "image":
      return "▣";
    case "pdf":
      return "▤";
    case "audio":
      return "♪";
    case "video":
      return "▶";
    // These two differ from the generic file glyph via the chip badge.
    case "spreadsheet":
      return "▦";
    case "document":
      return "▥";

    case "code":
      return "⟨⟩";
    case "file":
    default:
      return "▦";
  }
}

/**
 * JAISIU-4435 — the icon name (not the inline glyph) for an attachment.
 *
 * ## Why this exists
 *
 * Two icon vocabularies were in play and neither covered both:
 *
 *  - `session-files.ts::sessionFileIconName` switches on the **gateway**
 *    family (`SessionFileKind`). It has no `pdf` case, so a PDF — which
 *    the gateway classifies as `document` — always showed the generic
 *    document glyph. It also has no case for the **render** kinds, so
 *    `pdf` / `spreadsheet` / `code` / `file` fell through to `archive`
 *    (a generic box).
 *  - `attachmentIconForKind` switches on the **render** family but
 *    returns text glyphs (`▤ ▦ ▥`) that no chip actually renders.
 *
 * This function is the single decision point: one mapper, driven by the
 * **render** kind, that returns a name in the `icons` set
 * (`ui/src/ui/icons.ts`). Both the composer tray and the transcript
 * chips call it, so a PDF looks like a PDF everywhere.
 *
 * The gateway family is accepted too (a composer chip in flight carries
 * the coarse `kind` the store returned), and is mapped to the closest
 * render kind first:
 *
 *  - `tabular` → `spreadsheet` (Excel / CSV)
 *  - `document` → `document`
 *  - `text`    → `code`
 *  - `binary`  → `file`
 */
export function attachmentIconNameForKind(
  kind: AttachmentKind | SessionFileKind | string | undefined,
): IconName {
  switch (kind) {
    case "image":
      return "image";
    case "pdf":
      return "filePdf";
    case "video":
      return "film";
    case "audio":
      return "volume2";
    case "spreadsheet":
    case "tabular":
      return "fileSpreadsheet";
    case "document":
      return "fileText";
    case "code":
    case "text":
      return "fileCode";
    case "file":
    case "binary":
    default:
      return "archive";
  }
}

/**
 * Whether the in-app preview overlay (JAISIU-4015 / JAISIU-4427) can
 * show this attachment inline.
 *
 * `true` for every kind the overlay has a body renderer for:
 * `image`, `pdf`, `video`, `audio`, `spreadsheet`, `document`.
 * `false` for `code` and `file` — those have no body, so they download
 * directly rather than opening the overlay.
 *
 * JAISIU-4427 widened this from `{image, pdf}` so a video or audio
 * attachment is not silently downgraded to "download this" at click
 * time.
 *
 * Pure decision — no DOM, no `URL` constructor, no environment check.
 *
 * Spreadsheet and document kinds can also fall back to a download
 * page when the URL is not publicly reachable (Office Web Viewer
 * is the embed strategy for those kinds; see
 * `attachment-preview-overlay.ts::renderSpreadsheetBody` /
 * `renderDocumentBody`). The `attachmentIsPreviewable` check here
 * only routes the *chip click* to the viewer; the body renderer
 * makes the reachability decision.
 */
export function attachmentIsPreviewable(a: Pick<ChatAttachment, "kind">): boolean {
  return (
    a.kind === "image" ||
    a.kind === "pdf" ||
    a.kind === "video" ||
    a.kind === "audio" ||
    a.kind === "spreadsheet" ||
    a.kind === "document"
  );
}
